import { scoringConfigSchema, validateNoDuplicateKeys, validateNoLockedEdits, scoringErrorMessages } from "@/scoringUi";

/**
 * Runs the blocking scoring validations from spec 2 section 7 across every
 * scoring-capable criterion currently in the job-offer form: the two
 * recruitment-native sections (availability, financialExpectations), the
 * array sections wired into the same mechanism (employmentForms,
 * motivationQuestions, requiredDocuments, skills, experienceRequirements --
 * see jobOfferMapper.js), and any scenario-criterion overrides. Returns
 * business-language messages (never raw field/enum names) -- empty array
 * means the form may be submitted.
 *
 * @param {object} values - formik values (the job offer)
 * @param {Array<{criterionKey: string, scoring?: object, overrideMode?: string}>} [modelCriteria] - the raw `criteria` array from GET .../scoring-model (EffectiveCriterion[], PascalCase overrideMode as the API sends it) for the LOCKED-edit check. Omitted when the recruitment has no scenario yet.
 * @returns {string[]}
 */
export function collectScoringValidationErrors(values, modelCriteria) {
    const messages = new Set();

    const namedConfigs = [
        values.availability?.scoring,
        values.financialExpectations?.scoring,
        ...(values.employmentForms ?? []).map((ef) => ef.scoring),
        ...(values.motivationQuestions ?? []).map((mq) => mq.scoring),
        ...(values.requiredDocuments ?? []).map((rd) => rd.scoring),
        ...(values.skills ?? []).map((sk) => sk.scoring),
        ...(values.experienceRequirements ?? []).map((er) => er.scoring),
    ].filter(Boolean);

    for (const scoring of namedConfigs) {
        try {
            scoringConfigSchema.validateSync(scoring, { context: { maxScore: scoring.maxScore }, abortEarly: false });
        } catch (err) {
            for (const message of err.errors ?? [err.message]) messages.add(message);
        }
    }

    const overrideCriteria = (values.scoringOverrides ?? []).map((o) => ({ criterionKey: o.criterionKey, scoring: o.scoring }));
    const dupError = validateNoDuplicateKeys(overrideCriteria);
    if (dupError) messages.add(dupError);

    if (modelCriteria?.length) {
        // The API sends overrideMode as raw PascalCase ("Locked"); this package's
        // validateNoLockedEdits expects its own UPPER_SNAKE_CASE constant -- same
        // boundary mismatch apiMapping.js handles elsewhere, normalized inline here
        // since this is the one place that needs the *criterion-level* field (not
        // the nested scoring object apiMapping.js already covers).
        const normalizedScenarioCriteria = modelCriteria.map((c) => ({
            criterionKey: c.criterionKey,
            scoring: c.scoring,
            overrideMode: c.overrideMode === "Locked" ? "LOCKED" : c.overrideMode === "AllowRecruitmentOverride" ? "ALLOW_RECRUITMENT_OVERRIDE" : null,
        }));
        const lockedError = validateNoLockedEdits(overrideCriteria, normalizedScenarioCriteria);
        if (lockedError) messages.add(lockedError);
    }

    return Array.from(messages);
}

export { scoringErrorMessages };
